import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  ChevronRight,
  Download,
  FileText,
} from 'lucide-react'
import { Section } from '@/components/Section'
import { MachineCard } from '@/components/MachineCard'
import { LocalizedLink } from '@/components/LocalizedLink'
import { JsonLd } from '@/components/SEO/JsonLd'
import { usePageSEO } from '@/hooks/usePageSEO'
import { localizedRoutes } from '@/i18n/routes'
import { cn } from '@/lib/utils'
import {
  getMachineBySlug,
  getRelatedMachines,
  type Machine,
} from '@/data/machines'

/* ============================================================
   Helpers
   ============================================================ */

/**
 * A spec value may itself be a translation key (when the value needs to be
 * localised — e.g. "Linear reciprocating"). Detect that by inspecting whether
 * the first segment matches one of our `machinesData.*` namespaces.
 */
function useLocalisedSpecValue() {
  const { t } = useTranslation()
  return (raw: string): string => {
    if (raw.startsWith('machinesData.')) {
      return t(raw)
    }
    return raw
  }
}

/* ============================================================
   404 — "Machine not found" fallback
   ============================================================ */
function MachineNotFound({ slug }: { slug: string | undefined }) {
  const { t } = useTranslation()

  return (
    <main id="main-content" className="bg-ink-100">
      <section className="relative isolate overflow-hidden bg-ink-900 text-white pt-28 md:pt-36 pb-24 md:pb-28">
        {/* Diagonal stripe background */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.08] fx-diagonal-accent"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-40 top-1/2 h-96 w-96 -translate-y-1/2 rounded-full bg-brand-500/20 blur-3xl"
        />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            {/* Engineering coordinate */}
            <div className="mb-6 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.3em] text-brand-300">
              <span aria-hidden="true" className="block h-px w-8 bg-brand-500" />
              <span>{t('errorPage.code404')}</span>
            </div>

            <p className="font-display font-bold tabular-nums text-brand-500/80 text-7xl md:text-8xl leading-none">
              {t('machineDetail.notFound.code')}
            </p>
            <h1 className="mt-5 fx-text-balance font-bold text-3xl sm:text-4xl md:text-5xl leading-[1.1] text-white">
              {t('machineDetail.notFound.title')}
            </h1>
            <p className="mt-5 max-w-xl fx-text-pretty text-base md:text-lg leading-relaxed text-white/80">
              {t('machineDetail.notFound.body')}
            </p>

            {slug && (
              <p className="mt-4 inline-flex items-center gap-2 rounded-sm border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-white/70">
                <span className="block h-1.5 w-1.5 rounded-full bg-brand-400" aria-hidden="true" />
                {t('errorPage.slugLabel')} <span className="font-mono normal-case text-white/90">{slug}</span>
              </p>
            )}

            <div className="mt-10 flex flex-wrap gap-4">
              <LocalizedLink
                to="machines"
                className="group relative inline-flex items-center gap-3 rounded-sm bg-brand-500 px-6 py-3.5 text-sm font-bold uppercase tracking-[0.1em] text-white shadow-[0_10px_30px_-12px_rgba(74,155,220,0.55)] transition-[background-color,transform] duration-200 hover:bg-brand-400 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-900"
              >
                <span
                  aria-hidden="true"
                  className="absolute -left-[3px] top-1/2 block h-6 w-[3px] -translate-y-1/2 bg-white/90 transition-all duration-200 group-hover:h-8"
                />
                {t('machineDetail.notFound.backCta')}
                <ArrowRight className="h-4 w-4" strokeWidth={2.75} />
              </LocalizedLink>
              <LocalizedLink
                to="home"
                className="inline-flex items-center gap-2 rounded-sm border border-white/30 bg-white/5 px-6 py-3.5 text-sm font-bold uppercase tracking-[0.1em] text-white transition-colors duration-200 hover:border-brand-300 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-900"
              >
                <ArrowLeft className="h-4 w-4" strokeWidth={2.75} />
                {t('machineDetail.breadcrumbHome')}
              </LocalizedLink>
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}

/* ============================================================
   Breadcrumb component — industrial, block-based
   ============================================================ */
function Breadcrumb({ machineName }: { machineName: string }) {
  const { t } = useTranslation()
  return (
    <nav
      aria-label="Breadcrumb"
      className="border-b border-ink-200 bg-white"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4">
        <ol
          role="list"
          className="flex flex-wrap items-center gap-1.5 text-xs font-semibold tracking-[0.02em] text-ink-500"
        >
          <li>
            <LocalizedLink
              to="home"
              className="uppercase tracking-[0.14em] text-ink-600 transition-colors hover:text-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 rounded-sm"
            >
              {t('machineDetail.breadcrumbHome')}
            </LocalizedLink>
          </li>
          <li aria-hidden="true" className="text-ink-300">
            <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} />
          </li>
          <li>
            <LocalizedLink
              to="machines"
              className="uppercase tracking-[0.14em] text-ink-600 transition-colors hover:text-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 rounded-sm"
            >
              {t('machineDetail.breadcrumbMachines')}
            </LocalizedLink>
          </li>
          <li aria-hidden="true" className="text-ink-300">
            <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} />
          </li>
          <li
            aria-current="page"
            className="text-ink-900 font-bold tracking-normal normal-case"
          >
            {machineName}
          </li>
        </ol>
      </div>
    </nav>
  )
}

/* ============================================================
   Hero + gallery
   ============================================================ */
function MachineHero({
  machine,
  categoryLabel,
}: {
  machine: Machine
  categoryLabel: string
}) {
  const { t } = useTranslation()
  const gallery = useMemo(() => {
    if (machine.gallery && machine.gallery.length > 0) return machine.gallery
    return [machine.image]
  }, [machine])

  const [activeIndex, setActiveIndex] = useState(0)
  const activeImage = gallery[activeIndex] ?? machine.image
  const altText = machine.imageAltKey ? t(machine.imageAltKey) : machine.name
  const hasMultiple = gallery.length > 1

  return (
    <section
      aria-labelledby="machine-hero-title"
      className="relative isolate overflow-hidden bg-white"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Image column */}
          <div className="lg:col-span-7">
            <figure
              aria-label={t('machineDetail.galleryLabel')}
              className="relative isolate bg-ink-100"
            >
              {/* Corner ticks */}
              <div aria-hidden="true" className="absolute top-2 left-2 z-10 flex gap-0.5">
                <span className="block h-2 w-px bg-brand-500" />
                <span className="block h-5 w-px bg-brand-500" />
              </div>
              <div aria-hidden="true" className="absolute top-2 right-2 z-10 flex gap-0.5">
                <span className="block h-5 w-px bg-brand-500" />
                <span className="block h-2 w-px bg-brand-500" />
              </div>
              <div aria-hidden="true" className="absolute bottom-2 left-2 z-10 flex gap-0.5">
                <span className="block h-2 w-px bg-brand-500" />
                <span className="block h-5 w-px bg-brand-500" />
              </div>
              <div aria-hidden="true" className="absolute bottom-2 right-2 z-10 flex gap-0.5">
                <span className="block h-5 w-px bg-brand-500" />
                <span className="block h-2 w-px bg-brand-500" />
              </div>

              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={activeImage}
                  alt={altText}
                  className="h-full w-full object-cover transition-opacity duration-300"
                />
                {/* Category tag */}
                <span className="absolute bottom-4 left-4 rounded-sm bg-white/95 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-brand-700 shadow-sm">
                  {categoryLabel}
                </span>
              </div>
            </figure>

            {/* Thumbnails (when multiple images) */}
            {hasMultiple && (
              <div className="mt-4 flex gap-3">
                {gallery.map((img, i) => (
                  <button
                    key={img + i}
                    type="button"
                    onClick={() => setActiveIndex(i)}
                    aria-label={t('machineDetail.galleryThumbnailAlt', {
                      index: i + 1,
                      name: machine.name,
                    })}
                    aria-pressed={activeIndex === i}
                    className={cn(
                      'relative h-20 w-28 overflow-hidden border-2 transition-[border-color,transform] duration-200 rounded-sm',
                      activeIndex === i
                        ? 'border-brand-500 -translate-y-0.5'
                        : 'border-ink-200 hover:border-brand-300'
                    )}
                  >
                    <img
                      src={img}
                      alt=""
                      aria-hidden="true"
                      className="h-full w-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Overview column */}
          <div className="lg:col-span-5">
            {/* Eyebrow */}
            <div className="mb-4 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.22em] text-brand-600">
              <span aria-hidden="true" className="block h-px w-8 bg-brand-500" />
              {t('machineDetail.heroOverviewLabel')}
            </div>

            <h1
              id="machine-hero-title"
              className="fx-text-balance font-bold leading-[1.05] text-ink-900 text-3xl sm:text-4xl md:text-[2.5rem]"
            >
              {machine.name}
            </h1>

            <p className="mt-5 fx-text-pretty text-base md:text-lg leading-relaxed text-ink-600">
              {t(machine.shortDescriptionKey)}
            </p>

            {/* Quick specs snapshot — up to three */}
            {machine.specs && machine.specs.length > 0 && (
              <dl className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-px bg-ink-200 border border-ink-200">
                {machine.specs.slice(0, 3).map((spec) => (
                  <QuickSpec
                    key={spec.labelKey}
                    labelKey={spec.labelKey}
                    rawValue={spec.value}
                  />
                ))}
              </dl>
            )}

            {/* CTA row */}
            <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
              <LocalizedLink
                to="contacts"
                className="group relative inline-flex items-center justify-center gap-3 rounded-sm bg-brand-500 px-6 py-3.5 text-sm font-bold uppercase tracking-[0.1em] text-white shadow-[0_10px_30px_-12px_rgba(74,155,220,0.55)] transition-[background-color,transform] duration-200 hover:bg-brand-400 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2"
              >
                <span
                  aria-hidden="true"
                  className="absolute -left-[3px] top-1/2 block h-6 w-[3px] -translate-y-1/2 bg-white transition-all duration-200 group-hover:h-8"
                />
                {t('machineDetail.requestInfo')}
                <ArrowRight className="h-4 w-4" strokeWidth={2.75} />
              </LocalizedLink>
              {machine.catalogPdf && (
                <a
                  href={machine.catalogPdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center justify-center gap-3 rounded-sm border-2 border-brand-600 bg-white px-6 py-3.5 text-sm font-bold uppercase tracking-[0.1em] text-brand-700 transition-[background-color,color,transform] duration-200 hover:bg-brand-600 hover:text-white hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
                >
                  <Download className="h-4 w-4" strokeWidth={2.5} />
                  {t('buttons.downloadCatalog')}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom brand accent */}
      <div
        aria-hidden="true"
        className="absolute bottom-0 left-0 h-[3px] w-40 bg-brand-500"
      />
    </section>
  )
}

function QuickSpec({
  labelKey,
  rawValue,
}: {
  labelKey: string
  rawValue: string
}) {
  const { t } = useTranslation()
  const resolveValue = useLocalisedSpecValue()
  return (
    <div className="bg-white p-4 md:p-5">
      <dt className="text-[10px] font-bold uppercase tracking-[0.2em] text-ink-500">
        {t(`machinesData.specs.${labelKey}`)}
      </dt>
      <dd className="mt-1.5 font-display font-bold text-ink-900 text-base md:text-lg leading-tight">
        {resolveValue(rawValue)}
      </dd>
    </div>
  )
}

/* ============================================================
   Specs table
   ============================================================ */
function SpecsTable({ machine }: { machine: Machine }) {
  const { t } = useTranslation()
  const resolveValue = useLocalisedSpecValue()

  if (!machine.specs || machine.specs.length === 0) return null

  return (
    <Section tone="muted" spacing="md">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        <header className="lg:col-span-4">
          <div className="mb-3 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.22em] text-brand-600">
            <span aria-hidden="true" className="block h-px w-8 bg-brand-500" />
            {t('machineDetail.datasheet')}
          </div>
          <h2 className="fx-text-balance font-bold leading-tight text-ink-900 text-2xl md:text-3xl">
            {t('machineDetail.specs')}
          </h2>
          <p className="mt-3 text-sm md:text-base leading-relaxed text-ink-600 fx-text-pretty">
            {t('machineDetail.specsLead')}
          </p>
        </header>

        <div className="lg:col-span-8">
          <dl className="border border-ink-200 bg-white divide-y divide-ink-200">
            {machine.specs.map((spec, i) => (
              <div
                key={spec.labelKey}
                className={cn(
                  'grid grid-cols-1 sm:grid-cols-5 gap-2 sm:gap-4 p-4 md:p-5',
                  i % 2 === 1 && 'bg-ink-50/40'
                )}
              >
                <dt className="sm:col-span-2 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.2em] text-ink-500">
                  <span
                    aria-hidden="true"
                    className="block h-[3px] w-5 bg-brand-500 shrink-0"
                  />
                  {t(`machinesData.specs.${spec.labelKey}`)}
                </dt>
                <dd className="sm:col-span-3 font-display font-bold text-ink-900 text-base md:text-lg tabular-nums">
                  {resolveValue(spec.value)}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </Section>
  )
}

/* ============================================================
   Description + catalog side panel
   ============================================================ */
function DescriptionBlock({ machine }: { machine: Machine }) {
  const { t } = useTranslation()
  const body = t(machine.descriptionKey)
  const paragraphs = body
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean)

  return (
    <Section tone="default" spacing="lg">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        <div className="lg:col-span-8">
          <div className="mb-4 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.22em] text-brand-600">
            <span aria-hidden="true" className="block h-px w-8 bg-brand-500" />
            {t('machineDetail.features')}
          </div>
          <h2 className="fx-text-balance font-bold leading-tight text-ink-900 text-2xl md:text-3xl mb-6">
            {machine.name}
          </h2>
          <div className="space-y-5 text-base md:text-[17px] leading-[1.7] text-ink-700 fx-text-pretty">
            {paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>

        {/* Side info panel */}
        <aside className="lg:col-span-4 lg:sticky lg:top-24">
          <div className="relative border border-ink-200 bg-white p-6 md:p-7">
            {/* Top brand rule */}
            <div
              aria-hidden="true"
              className="absolute top-0 left-0 h-[3px] w-16 bg-brand-500"
            />
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand-600 mb-3">
              {t('machineDetail.requestInfo')}
            </p>
            <p className="text-sm md:text-base leading-relaxed text-ink-600 fx-text-pretty mb-5">
              {t('machineDetail.requestInfoBody')}
            </p>
            <LocalizedLink
              to="contacts"
              className="group inline-flex w-full items-center justify-between gap-2 rounded-sm bg-brand-500 px-5 py-3 text-sm font-bold uppercase tracking-[0.08em] text-white transition-[background-color,transform] duration-200 hover:bg-brand-400 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2"
            >
              {t('buttons.contactUs')}
              <ArrowUpRight className="h-4 w-4" strokeWidth={2.75} />
            </LocalizedLink>

            {machine.catalogPdf && (
              <a
                href={machine.catalogPdf}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 group inline-flex w-full items-center gap-3 rounded-sm border border-ink-200 bg-white px-5 py-3 text-sm font-semibold text-ink-700 transition-[background-color,border-color,color] duration-200 hover:border-brand-400 hover:text-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
              >
                <span
                  aria-hidden="true"
                  className="flex h-9 w-9 items-center justify-center rounded-sm bg-ink-100 text-brand-600 group-hover:bg-brand-500 group-hover:text-white transition-colors"
                >
                  <FileText className="h-4 w-4" strokeWidth={2.25} />
                </span>
                <span className="flex-1 leading-tight">
                  <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-ink-500 group-hover:text-brand-600">
                    PDF
                  </span>
                  <span className="block text-sm font-bold text-ink-900 group-hover:text-brand-700">
                    {t('buttons.downloadCatalog')}
                  </span>
                </span>
                <Download
                  className="h-4 w-4 text-ink-500 group-hover:text-brand-600 transition-colors"
                  strokeWidth={2.25}
                />
              </a>
            )}
          </div>
        </aside>
      </div>
    </Section>
  )
}

/* ============================================================
   Related machines
   ============================================================ */
function RelatedMachines({ machine }: { machine: Machine }) {
  const { t } = useTranslation()
  const related = getRelatedMachines(machine, 3)
  if (related.length === 0) return null

  return (
    <Section tone="muted" spacing="lg">
      <header className="mb-10 md:mb-12 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="max-w-2xl">
          <div className="mb-3 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.22em] text-brand-600">
            <span aria-hidden="true" className="block h-px w-8 bg-brand-500" />
            {t('machineDetail.related')}
          </div>
          <h2 className="fx-text-balance font-bold leading-tight text-ink-900 text-2xl md:text-3xl">
            {t('machineDetail.related')}
          </h2>
          <p className="mt-3 text-base leading-relaxed text-ink-600 fx-text-pretty">
            {t('machineDetail.relatedLead')}
          </p>
        </div>
        <LocalizedLink
          to="machines"
          className="group inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-brand-600 transition-colors hover:text-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 rounded-sm"
        >
          {t('machineDetail.back')}
          <ArrowRight
            className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1"
            strokeWidth={2.75}
          />
        </LocalizedLink>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
        {related.map((m) => {
          const categoryLabel =
            m.category === 'tangenziali'
              ? t('machines.categories.tangentialEyebrow')
              : t('machines.categories.doubleColumnEyebrow')
          return (
            <MachineCard
              key={m.slug}
              slug={m.slug}
              image={m.image}
              imageAlt={m.imageAltKey ? t(m.imageAltKey) : m.name}
              name={m.name}
              description={t(m.shortDescriptionKey)}
              category={categoryLabel}
            />
          )
        })}
      </div>
    </Section>
  )
}

/* ============================================================
   Main component
   ============================================================ */
export default function MachineDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const { t, i18n } = useTranslation()
  const currentLang = (i18n.language === 'en' ? 'en' : 'it') as 'it' | 'en'

  const machine = useMemo(
    () => (slug ? getMachineBySlug(slug) : undefined),
    [slug]
  )

  // SEO — dynamic based on current machine. Must be called before any early
  // return to respect the Rules of Hooks. For detail pages we pass explicit
  // alternate paths so the IT↔EN hreflang pair carries the slug.
  const alternatePaths = useMemo(() => {
    if (!machine) return undefined
    return {
      it: `/it/${localizedRoutes.machines.it}/${machine.slug}`,
      en: `/en/${localizedRoutes.machines.en}/${machine.slug}`,
    }
  }, [machine])

  usePageSEO({
    title: machine
      ? `${machine.name} | ${t('meta.companyName')}`
      : `${t('machineDetail.notFound.title')} | ${t('meta.companyName')}`,
    description: machine
      ? t(machine.shortDescriptionKey)
      : t('machineDetail.notFound.body'),
    ogTitle: machine
      ? `${machine.name} — ${t('meta.companyName')}`
      : `${t('machineDetail.notFound.title')} — ${t('meta.companyName')}`,
    ogDescription: machine
      ? t(machine.shortDescriptionKey)
      : t('machineDetail.notFound.body'),
    ogImage: machine?.image ?? '/images/machines/fenix-ff-u-200.jpg',
    ogType: machine ? 'product' : 'website',
    alternatePaths,
  })

  if (!machine) {
    return <MachineNotFound slug={slug} />
  }

  const categoryLabel =
    machine.category === 'tangenziali'
      ? t('machines.categories.tangentialEyebrow')
      : t('machines.categories.doubleColumnEyebrow')

  const origin =
    typeof window !== 'undefined'
      ? window.location.origin
      : 'https://fenixgrind.it'
  const machinesPath = `/${currentLang}/${localizedRoutes.machines[currentLang]}`
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: t('machineDetail.breadcrumbHome'),
        item: `${origin}/${currentLang}`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: t('machineDetail.breadcrumbMachines'),
        item: `${origin}${machinesPath}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: machine.name,
        item: `${origin}${machinesPath}/${machine.slug}`,
      },
    ],
  }

  return (
    <main id="main-content" className="bg-ink-100">
      <JsonLd id="breadcrumb" data={breadcrumbSchema} />
      <Breadcrumb machineName={machine.name} />
      <MachineHero machine={machine} categoryLabel={categoryLabel} />
      <DescriptionBlock machine={machine} />
      <SpecsTable machine={machine} />
      <RelatedMachines machine={machine} />
    </main>
  )
}
